import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { View, Text, StyleSheet, TouchableOpacity, TextInput, ActivityIndicator, ScrollView, Animated } from 'react-native';
import { FaTimes, FaCopy, FaTwitter, FaTelegram, FaCheck, FaRocket, FaChevronDown, FaChevronUp, FaLightbulb, FaLayerGroup } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import PercentageLoader from './PercentageLoader';
import ListsManager from '../services/ListsManager';
import { useTheme } from '../theme/ThemeContext';

let THEME = {};
const getStyles = () => StyleSheet.create({
    overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, justifyContent: 'center', alignItems: 'center', zIndex: 10000 },
    backdrop: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: THEME.overlay, backdropFilter: 'blur(8px)', zIndex: -1 },
    modalContainer: { width: '90%', maxWidth: 900, height: '85vh', backgroundColor: THEME.bg, borderRadius: 24, borderWidth: 1, borderColor: THEME.border, overflow: 'hidden', boxShadow: '0 40px 80px rgba(0,0,0,0.6)', display: 'flex', flexDirection: 'column' },
    header: { padding: 24, borderBottomWidth: 1, borderBottomColor: THEME.border, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', backgroundColor: THEME.bgCard },
    badge: { backgroundColor: 'rgba(10,132,255,0.1)', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 6 },
    badgeText: { color: '#0a84ff', fontSize: 10, fontWeight: '800', letterSpacing: 0.5 },
    title: { color: THEME.textPrimary, fontSize: 20, fontWeight: '700', letterSpacing: -0.5 },
    subtitle: { color: THEME.textSecondary, fontSize: 14, marginTop: 4 },
    closeButton: { padding: 8, backgroundColor: THEME.bgSurface, borderRadius: 20 },
    tabs: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 24, borderBottomWidth: 1, borderBottomColor: THEME.border, backgroundColor: THEME.bg },
    tabItem: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 16, borderBottomWidth: 2, borderBottomColor: 'transparent' },
    activeTabItem: { borderBottomColor: '#0a84ff' },
    stepNum: { width: 24, height: 24, borderRadius: 12, backgroundColor: THEME.bgSurface, justifyContent: 'center', alignItems: 'center' },
    activeStepNum: { backgroundColor: 'rgba(10,132,255,0.15)' },
    stepNumText: { fontSize: 12, fontWeight: '700', color: THEME.textMuted },
    tabText: { fontSize: 14, fontWeight: '600', color: THEME.textMuted },
    activeTabText: { color: THEME.textPrimary },
    divider: { width: 1, height: 20, backgroundColor: THEME.border, marginHorizontal: 20 },
    content: { flex: 1, backgroundColor: THEME.bg },
    loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 60 },
    loadingText: { color: THEME.textMuted, marginTop: 24, fontSize: 16 },
    strategyContainer: { gap: 24 },
    section: {},
    sectionHeader: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 12 },
    sectionTitle: { color: THEME.textSecondary, fontSize: 12, fontWeight: '700', letterSpacing: 1 },
    charCount: { color: THEME.textMuted, fontSize: 12, fontFamily: 'monospace' },
    mainInput: { backgroundColor: THEME.bgCard, color: THEME.textPrimary, fontSize: 16, lineHeight: 24, padding: 20, borderRadius: 16, minHeight: 140, textAlignVertical: 'top', borderWidth: 1, borderColor: THEME.border },
    actionRow: { flexDirection: 'row', justifyContent: 'flex-end', gap: 12, marginTop: 12 },
    secondaryBtn: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 16, paddingVertical: 10, backgroundColor: THEME.bgSurface, borderRadius: 10 },
    btnText: { color: THEME.textSecondary, fontSize: 13, fontWeight: '600' },
    primaryBtn: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 20, paddingVertical: 10, backgroundColor: '#0a84ff', borderRadius: 10 },
    primaryBtnText: { color: '#fff', fontSize: 13, fontWeight: '700' },
    collapsibleContainer: { borderWidth: 1, borderColor: THEME.border, borderRadius: 16, overflow: 'hidden', backgroundColor: THEME.bg },
    collapsibleHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 16, backgroundColor: THEME.bgCard },
    iconBadge: { width: 24, height: 24, borderRadius: 6, backgroundColor: 'rgba(255, 214, 10, 0.1)', justifyContent: 'center', alignItems: 'center' },
    collapsibleTitle: { color: THEME.textPrimary, fontSize: 14, fontWeight: '600' },
    collapsibleContent: { padding: 20, gap: 16 },
    label: { color: THEME.textMuted, fontSize: 11, fontWeight: '700', marginBottom: 8, letterSpacing: 0.5 },
    subInput: { backgroundColor: THEME.bg, color: THEME.textSecondary, padding: 12, borderRadius: 8, fontSize: 14, borderWidth: 1, borderColor: THEME.border, minHeight: 60 },
    staticText: { color: THEME.textSecondary, fontSize: 14, lineHeight: 20 },
    nextPhaseContainer: { backgroundColor: THEME.bgCard, borderRadius: 20, padding: 24, borderWidth: 1, borderColor: THEME.border },
    nextPhaseHeader: { flexDirection: 'row', gap: 16, marginBottom: 20 },
    iconCircle: { width: 40, height: 40, borderRadius: 20, backgroundColor: 'rgba(10,132,255,0.1)', justifyContent: 'center', alignItems: 'center' },
    nextPhaseTitle: { color: THEME.textPrimary, fontSize: 16, fontWeight: '700' },
    nextPhaseDesc: { color: THEME.textMuted, fontSize: 13 },
    controlsRow: { flexDirection: 'row', gap: 12 },
    counter: { flexDirection: 'row', alignItems: 'center', backgroundColor: THEME.bg, borderRadius: 12, paddingHorizontal: 16, borderWidth: 1, borderColor: THEME.border, gap: 12 },
    counterLabel: { color: THEME.textMuted, fontSize: 13, fontWeight: '600' },
    counterInput: { color: THEME.textPrimary, fontSize: 16, fontWeight: '700', width: 30, textAlign: 'center' },
    generateBtn: { flex: 1, flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: 10, backgroundColor: THEME.bgSurface, borderRadius: 12, paddingVertical: 14, borderWidth: 1, borderColor: THEME.border },
    generateBtnText: { color: THEME.textPrimary, fontSize: 15, fontWeight: '700' },
    grid: { flexDirection: 'row', flexWrap: 'wrap', gap: 16 },
    gridCard: { width: '48%', backgroundColor: THEME.bgCard, padding: 20, borderRadius: 16, borderWidth: 1, borderColor: THEME.border },
    cardTop: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 12 },
    cardIndex: { color: THEME.textMuted, fontSize: 12, fontWeight: '800' },
    cardText: { color: THEME.textPrimary, fontSize: 14, lineHeight: 20 },
    emptyContainer: { alignItems: 'center', padding: 40 },
    emptyText: { color: THEME.textMuted, fontSize: 14 },
    textBtn: { padding: 10 },
    errorContainer: { alignItems: 'center', justifyContent: 'center', paddingVertical: 40 },
    errorText: { color: '#ff453a', fontSize: 16, textAlign: 'center', marginBottom: 20 },
    retryBtn: { paddingHorizontal: 20, paddingVertical: 12, borderRadius: 12, backgroundColor: 'rgba(10, 132, 255, 0.15)' },
    retryText: { color: '#0a84ff', fontSize: 16, fontWeight: '600' },
    row: { flexDirection: 'row', gap: 16 },
});
let styles = getStyles();

/**
 * CollapsibleSection - Helper for hiding dense content
 */
const CollapsibleSection = ({ title, children, defaultExpanded = false, icon }) => {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const { theme } = useTheme();
    Object.assign(THEME, theme);
    styles = getStyles();

    return (
        <View style={styles.collapsibleContainer}>
            <TouchableOpacity
                style={styles.collapsibleHeader}
                onPress={() => setExpanded(!expanded)}
                activeOpacity={0.7}
            >
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                    {icon && <View style={styles.iconBadge}>{icon}</View>}
                    <Text style={styles.collapsibleTitle}>{title}</Text>
                </View>
                {expanded ? <FaChevronUp color={THEME.textMuted} size={12} /> : <FaChevronDown color={THEME.textMuted} size={12} />}
            </TouchableOpacity>

            {expanded && (
                <View style={styles.collapsibleContent}>
                    {children}
                </View>
            )}
        </View>
    );
};

/**
 * CounterNarrativeModal - Professional, Wider, Collapsible
 */
const CounterNarrativeModal = ({
    isVisible,
    onClose,
    counterNarrativeData = null,
    isLoading = false,
    onRegenerate,
    platform = 'twitter',
    error = null,
}) => {
    // Phase 2 State
    const [supportPosts, setSupportPosts] = useState([]);
    const [isGeneratingSupport, setIsGeneratingSupport] = useState(false);
    const [supportError, setSupportError] = useState(null);

    // UI State
    const [activeTab, setActiveTab] = useState('strategy'); // 'strategy', 'campaign'

    // Editable Fields
    const [editedMainPost, setEditedMainPost] = useState('');
    const [editedCoreMessage, setEditedCoreMessage] = useState('');
    const [postCount, setPostCount] = useState(20);

    // Utils
    const [copiedPost, setCopiedPost] = useState(null);

    useEffect(() => {
        if (counterNarrativeData) {
            setEditedMainPost(counterNarrativeData.main_post || '');
            setEditedCoreMessage(counterNarrativeData.core_message || '');
            setSupportPosts(counterNarrativeData.support_posts || []);
            setPostCount(20);
            setSupportError(null);
            setIsGeneratingSupport(false);
            setActiveTab('strategy');
        }
    }, [counterNarrativeData]);

    const { t } = useTranslation();
    const { theme } = useTheme();
    Object.assign(THEME, theme);
    styles = getStyles();

    if (!isVisible) return null;

    const handleGenerateSupportPosts = async () => {
        setIsGeneratingSupport(true);
        setSupportError(null);
        setActiveTab('campaign');
        // Use EDITED core message
        console.log("Saving strategy updates:", {
            id: counterNarrativeData?.id,
            core: editedCoreMessage,
            main: editedMainPost
        });

        try {
            const result = await ListsManager.generateSupportPosts(
                editedCoreMessage,
                counterNarrativeData?.tone_guide || 'Professional',
                postCount,
                counterNarrativeData?.id, // Pass ID for update
                editedMainPost, // Pass updated main post
                // We don't have an edited explanation field yet, so we skip it or pass original
                // The router COALESCEs, so passing undefined/null is fine to keep original
                null,
                counterNarrativeData?.strategy_name // Pass strategy name
            );

            if (result.success) {
                setSupportPosts(result.posts || []);
            } else {
                setSupportError(result.error_message);
            }
        } catch (err) {
            setSupportError(err.message);
        } finally {
            setIsGeneratingSupport(false);
        }
    };

    const copyToClipboard = async (text, id) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedPost(id);
            setTimeout(() => setCopiedPost(null), 2000);
        } catch (e) {
            console.error(e);
        }
    };

    const handleOpenPlatform = () => {
        const text = encodeURIComponent(editedMainPost);
        const url = platform === 'twitter'
            ? `https://twitter.com/intent/tweet?text=${text}`
            : `https://t.me/share/url?text=${text}`;
        window.open(url, '_blank');
    };

    return ReactDOM.createPortal(
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 199999 }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: THEME.overlay, backdropFilter: 'blur(8px)' }} onClick={onClose} />

            <View style={[styles.modalContainer, { zIndex: 1 }]}>
                {/* Header */}
                <View style={styles.header}>
                    <View>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                            <View style={styles.badge}>
                                <Text style={styles.badgeText}>{t('app.counterNarrative.phase1Badge')}</Text>
                            </View>
                            <Text style={styles.title}>{t('app.counterNarrative.title')}</Text>
                        </View>
                        <Text style={styles.subtitle}>
                            {counterNarrativeData?.narrative_title ? `Re: ${counterNarrativeData.narrative_title}` : t('app.counterNarrative.commandCenter')}
                        </Text>
                        <Text style={[styles.subtitle, { marginTop: 2, fontSize: 13, color: '#0a84ff' }]}>
                            {counterNarrativeData?.strategy_name || t('app.counterNarrative.formulatingStrategy')}
                        </Text>
                    </View>

                    <TouchableOpacity onPress={onClose} style={styles.closeButton}>
                        <FaTimes size={16} color="#8e8e93" />
                    </TouchableOpacity>
                </View>

                {/* Tabs / Progress */}
                <View style={styles.tabs}>
                    <TouchableOpacity
                        style={[styles.tabItem, activeTab === 'strategy' && styles.activeTabItem]}
                        onPress={() => setActiveTab('strategy')}
                    >
                        <View style={[styles.stepNum, activeTab === 'strategy' ? styles.activeStepNum : {}]}>
                            <Text style={[styles.stepNumText, activeTab === 'strategy' ? { color: '#0a84ff' } : {}]}>1</Text>
                        </View>
                        <Text style={[styles.tabText, activeTab === 'strategy' && styles.activeTabText]}>{t('app.counterNarrative.strategyTab')}</Text>
                    </TouchableOpacity>

                    <View style={styles.divider} />

                    <TouchableOpacity
                        style={[styles.tabItem, activeTab === 'campaign' && styles.activeTabItem]}
                        onPress={() => setActiveTab('campaign')}
                    >
                        <View style={[styles.stepNum, activeTab === 'campaign' ? styles.activeStepNum : {}]}>
                            <Text style={[styles.stepNumText, activeTab === 'campaign' ? { color: '#0a84ff' } : {}]}>2</Text>
                        </View>
                        <Text style={[styles.tabText, activeTab === 'campaign' && styles.activeTabText]}>
                            {t('app.counterNarrative.supportCampaignTab')} {supportPosts.length > 0 && `(${supportPosts.length})`}
                        </Text>
                    </TouchableOpacity>
                </View>

                {/* Content Area */}
                <ScrollView
                    style={styles.content}
                    contentContainerStyle={{ padding: 30 }}
                    showsVerticalScrollIndicator={false}
                >
                    {isLoading ? (
                        <View style={styles.loadingContainer}>
                            <PercentageLoader fontSize={48} color="#0a84ff" />
                            <Text style={styles.loadingText}>{t('app.counterNarrative.analyzingVectors')}</Text>
                        </View>
                    ) : error ? (
                        <View style={styles.errorContainer}>
                            <Text style={styles.errorText}>{error}</Text>
                            <TouchableOpacity onPress={onRegenerate} style={styles.retryBtn}>
                                <Text style={styles.retryText}>{t('app.counterNarrative.retryGeneration')}</Text>
                            </TouchableOpacity>
                        </View>
                    ) : (
                        <>
                            {activeTab === 'strategy' && (
                                <View style={styles.strategyContainer}>

                                    {/* Main Post - Most Important */}
                                    <View style={styles.section}>
                                        <View style={styles.sectionHeader}>
                                            <Text style={styles.sectionTitle}>{t('app.counterNarrative.mainTweetHook')}</Text>
                                            <Text style={styles.charCount}>{t('app.counterNarrative.charCount', { n: editedMainPost.length })}</Text>
                                        </View>
                                        <TextInput
                                            style={styles.mainInput}
                                            value={editedMainPost}
                                            onChangeText={setEditedMainPost}
                                            multiline
                                            placeholder={t('app.counterNarrative.draftPlaceholder')}
                                            placeholderTextColor={theme.inputPlaceholder}
                                        />
                                        <View style={styles.actionRow}>
                                            <TouchableOpacity
                                                style={styles.secondaryBtn}
                                                onPress={() => copyToClipboard(editedMainPost, 'main')}
                                            >
                                                {copiedPost === 'main' ? <FaCheck color="#30d158" /> : <FaCopy />}
                                                <Text style={styles.btnText}>{copiedPost === 'main' ? t('app.counterNarrative.copied') : t('app.counterNarrative.copy')}</Text>
                                            </TouchableOpacity>

                                            <TouchableOpacity
                                                style={styles.primaryBtn}
                                                onPress={handleOpenPlatform}
                                            >
                                                {platform === 'twitter' ? <FaTwitter /> : <FaTelegram />}
                                                <Text style={styles.primaryBtnText}>{platform === 'twitter' ? t('app.counterNarrative.postToX') : t('app.counterNarrative.postToTelegram')}</Text>
                                            </TouchableOpacity>
                                        </View>
                                    </View>

                                    {/* Collapsible Strategy Details */}
                                    <CollapsibleSection
                                        title={t('app.counterNarrative.strategicContext')}
                                        icon={<FaLightbulb color="#FFD60A" size={10} />}
                                    >
                                        <View style={styles.subsection}>
                                            <Text style={styles.label}>{t('app.counterNarrative.coreMessageLabel')}</Text>
                                            <TextInput
                                                style={styles.subInput}
                                                value={editedCoreMessage}
                                                onChangeText={setEditedCoreMessage}
                                                multiline
                                            />
                                        </View>

                                        <View style={styles.row}>
                                            <View style={{ flex: 1 }}>
                                                <Text style={styles.label}>{t('app.counterNarrative.toneLabel')}</Text>
                                                <Text style={styles.staticText}>{counterNarrativeData?.tone_guide}</Text>
                                            </View>
                                        </View>

                                        <View style={{ marginTop: 16 }}>
                                            <Text style={styles.label}>{t('app.counterNarrative.whyThisWorks')}</Text>
                                            <Text style={styles.staticText}>{counterNarrativeData?.explanation}</Text>
                                        </View>
                                    </CollapsibleSection>

                                    {/* Phase 2 CTA */}
                                    <View style={styles.nextPhaseContainer}>
                                        <View style={styles.nextPhaseHeader}>
                                            <View style={styles.iconCircle}>
                                                <FaLayerGroup color="#0a84ff" size={14} />
                                            </View>
                                            <View>
                                                <Text style={styles.nextPhaseTitle}>{t('app.counterNarrative.mobilizeSupport')}</Text>
                                                <Text style={styles.nextPhaseDesc}>{t('app.counterNarrative.mobilizeSupportDesc')}</Text>
                                            </View>
                                        </View>

                                        <View style={styles.controlsRow}>
                                            <View style={styles.counter}>
                                                <Text style={styles.counterLabel}>{t('app.counterNarrative.quantity')}</Text>
                                                <TextInput
                                                    style={styles.counterInput}
                                                    value={String(postCount)}
                                                    onChangeText={(val) => setPostCount(Number(val) || 0)}
                                                    keyboardType="numeric"
                                                />
                                            </View>

                                            <TouchableOpacity
                                                style={[styles.generateBtn, isGeneratingSupport && { opacity: 0.7 }]}
                                                onPress={handleGenerateSupportPosts}
                                                disabled={isGeneratingSupport}
                                            >
                                                {isGeneratingSupport ? (
                                                    <ActivityIndicator color="#000" size="small" />
                                                ) : (
                                                    <FaRocket />
                                                )}
                                                <Text style={styles.generateBtnText}>
                                                    {isGeneratingSupport ? t('app.counterNarrative.generating') : t('app.counterNarrative.generatePosts')}
                                                </Text>
                                            </TouchableOpacity>
                                        </View>
                                    </View>
                                </View>
                            )}

                            {activeTab === 'campaign' && (
                                <View style={styles.campaignContainer}>
                                    {isGeneratingSupport ? (
                                        <View style={styles.loadingContainer}>
                                            <ActivityIndicator size="large" color="#0a84ff" />
                                            <Text style={styles.loadingText}>{t('app.counterNarrative.generatingAngles', { n: postCount })}</Text>
                                        </View>
                                    ) : supportPosts.length > 0 ? (
                                        <View style={styles.grid}>
                                            {supportPosts.map((post, idx) => (
                                                <View key={idx} style={styles.gridCard}>
                                                    <View style={styles.cardTop}>
                                                        <Text style={styles.cardIndex}>#{idx + 1}</Text>
                                                        <TouchableOpacity onPress={() => copyToClipboard(post, idx)}>
                                                            {copiedPost === idx ? <FaCheck color="#30d158" size={12} /> : <FaCopy color="#555" size={12} />}
                                                        </TouchableOpacity>
                                                    </View>
                                                    <Text style={styles.cardText}>{post}</Text>
                                                </View>
                                            ))}
                                        </View>
                                    ) : (
                                        <View style={styles.emptyContainer}>
                                            <Text style={styles.emptyText}>{t('app.counterNarrative.noPostsYet')}</Text>
                                            <TouchableOpacity onPress={() => setActiveTab('strategy')} style={styles.textBtn}>
                                                <Text style={{ color: '#0a84ff' }}>{t('app.counterNarrative.goBackToStrategy')}</Text>
                                            </TouchableOpacity>
                                        </View>
                                    )}
                                </View>
                            )}
                        </>
                    )}
                </ScrollView>
            </View>
        </div>,
        document.body
    );
};


export default CounterNarrativeModal;
